import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { addDoc, collection, updateDoc } from "firebase/firestore";
import { Header } from "../../components/Header/Header";
import { Footer } from "../../components/Footer/Footer";
import { useAuth } from "../../../context/AuthContext";
import { db } from "../../../service/firebase";
import "./CreateTurma.css";

const daysOfWeek = [
  "Segunda-feira",
  "Terça-feira",
  "Quarta-feira",
  "Quinta-feira",
  "Sexta-feira",
  "Sábado",
  "Domingo",
];

const CreateTurma = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedDays, setSelectedDays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [formData, setFormData] = useState({
    nome: "",
    descricao: "",
    regras: "",
    local: "",
    qtdJogadoresTime: "",
    tempoPartida: "",
    maxPontosPartida: "",
    maxVitoriasSeguidas: "0",
    dadosIndividuais: false,
    corTime1: "#169e2b",
    corTime2: "#000000",
    protegida: false,
    senhaGrupo: "",
  });

  const isFormValid = useMemo(() => {
    return (
      formData.nome &&
      formData.descricao &&
      formData.regras &&
      formData.local &&
      formData.qtdJogadoresTime &&
      formData.tempoPartida &&
      formData.maxPontosPartida &&
      formData.maxVitoriasSeguidas !== "" &&
      selectedDays.length > 0 &&
      (!formData.protegida || formData.senhaGrupo)
    );
  }, [formData, selectedDays]);

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const toggleDay = (day) => {
    setSelectedDays((current) =>
      current.includes(day)
        ? current.filter((d) => d !== day)
        : [...current, day]
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!user) {
      setError("Você precisa estar autenticado para criar uma turma.");
      return;
    }

    if (formData.protegida && !formData.senhaGrupo) {
      setError("Defina uma senha para turmas protegidas.");
      return;
    }

    if (!isFormValid) {
      setError(
        "Preencha todos os campos e selecione pelo menos um dia da semana."
      );
      return;
    }

    setError("");
    setLoading(true);

    try {
      const turmaPayload = {
        nome: formData.nome,
        descricao: formData.descricao,
        regras: formData.regras,
        dias_semana: selectedDays,
        local: formData.local,
        qtd_jogadores_time: parseInt(formData.qtdJogadoresTime, 10),
        tempo_de_partida: parseInt(formData.tempoPartida, 10),
        max_pontos_partida: parseInt(formData.maxPontosPartida, 10),
        max_vitorias: parseInt(formData.maxVitoriasSeguidas || "0", 10) || 0,
        dados_individuais: formData.dadosIndividuais,
        cor_time_1: formData.corTime1,
        cor_time_2: formData.corTime2,
        protegida: formData.protegida,
        senha_grupo: formData.protegida ? formData.senhaGrupo : "",
        status: 1,
        created_by: user.uid,
      };

      const turmaRef = await addDoc(collection(db, "turmas"), turmaPayload);
      await updateDoc(turmaRef, { id: turmaRef.id });

      const jogadorPayload = {
        id_turma: turmaRef.id,
        id_usuario: user.uid,
        admin: true,
        status: 1,
        gols: 0,
        assistencias: 0,
        vitorias: 0,
        empates: 0,
        derrotas: 0,
        qt_partidas: 0,
        jogosSemTomarGol: 0,
        influenciaVitoria: 0,
        overall: 65,
      };

      await addDoc(collection(db, "jogador_turma"), jogadorPayload);
      navigate("/turmas");
    } catch (creationError) {
      console.error("Erro ao criar turma", creationError);
      setError(
        "Não foi possível salvar a turma. Tente novamente em alguns instantes."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Header />
      <div className="body">
        <div className="center">
          <div className="body-content card create-turma-card">
            <div className="topo-page">
              <strong># Nova Turma</strong>
            </div>
            <p className="create-turma-description">
              Defina as regras e informações principais da sua turma fixa de
              amistosos.
            </p>
            <form className="create-turma-form" onSubmit={handleSubmit}>
              {error && (
                <p className="create-turma-error" role="alert">
                  {error}
                </p>
              )}
              <label htmlFor="nome">Nome</label>
              <input
                id="nome"
                name="nome"
                type="text"
                value={formData.nome}
                onChange={handleInputChange}
              />

              <label htmlFor="descricao">Descrição</label>
              <textarea
                id="descricao"
                name="descricao"
                value={formData.descricao}
                onChange={handleInputChange}
              />

              <label htmlFor="regras">Regras</label>
              <textarea
                id="regras"
                name="regras"
                value={formData.regras}
                onChange={handleInputChange}
              />

              <label>Dias da semana</label>
              <div className="days-grid">
                {daysOfWeek.map((day) => (
                  <label key={day} className="day-option">
                    <input
                      type="checkbox"
                      checked={selectedDays.includes(day)}
                      onChange={() => toggleDay(day)}
                    />
                    <span>{day}</span>
                  </label>
                ))}
              </div>

              <label htmlFor="local">Local</label>
              <input
                id="local"
                name="local"
                type="text"
                value={formData.local}
                onChange={handleInputChange}
              />

              <label htmlFor="qtdJogadoresTime">Qtd. jogadores por time</label>
              <input
                id="qtdJogadoresTime"
                name="qtdJogadoresTime"
                type="number"
                min="1"
                value={formData.qtdJogadoresTime}
                onChange={handleInputChange}
              />

              <label htmlFor="tempoPartida">Tempo de partida (min)</label>
              <input
                id="tempoPartida"
                name="tempoPartida"
                type="number"
                min="1"
                value={formData.tempoPartida}
                onChange={handleInputChange}
              />

              <label htmlFor="maxPontosPartida">Máx. pontos por partida</label>
              <input
                id="maxPontosPartida"
                name="maxPontosPartida"
                type="number"
                min="1"
                value={formData.maxPontosPartida}
                onChange={handleInputChange}
              />

              <label htmlFor="maxVitoriasSeguidas">Máx. vitórias seguidas</label>
              <input
                id="maxVitoriasSeguidas"
                name="maxVitoriasSeguidas"
                type="number"
                min="0"
                value={formData.maxVitoriasSeguidas}
                onChange={handleInputChange}
              />

              <label className="toggle-field">
                <input
                  type="checkbox"
                  name="dadosIndividuais"
                  checked={formData.dadosIndividuais}
                  onChange={handleInputChange}
                />
                Registrar dados individuais
              </label>

              <div className="turma-security-section">
                <label className="toggle-field">
                  <input
                    type="checkbox"
                    name="protegida"
                    checked={formData.protegida}
                    onChange={handleInputChange}
                  />
                  Proteger turma com senha
                </label>
                {formData.protegida && (
                  <>
                    <label htmlFor="senhaGrupo">Senha da turma</label>
                    <input
                      id="senhaGrupo"
                      name="senhaGrupo"
                      type="password"
                      placeholder="Digite uma senha segura"
                      value={formData.senhaGrupo}
                      onChange={handleInputChange}
                    />
                  </>
                )}
              </div>

              <div className="colors-grid">
                <label htmlFor="corTime1">Cor Time 1</label>
                <input
                  id="corTime1"
                  name="corTime1"
                  className="input-color"
                  type="color"
                  value={formData.corTime1}
                  onChange={handleInputChange}
                />
                <label htmlFor="corTime2">Cor Time 2</label>
                <input
                  id="corTime2"
                  name="corTime2"
                  className="input-color"
                  type="color"
                  value={formData.corTime2}
                  onChange={handleInputChange}
                />
              </div>

              <button
                type="submit"
                className="btn-default"
                disabled={!isFormValid || loading}
              >
                {loading ? "Salvando..." : "Salvar turma"}
              </button>
            </form>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default CreateTurma;
